import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";
import BusinessIcon from "@material-ui/icons/Business";
import SyncIcon from "@material-ui/icons/Sync";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MetaTemplatesManager from "../../components/MetaTemplatesManager";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles(theme => ({
  paper: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  chipReady: { backgroundColor: "#c8e6c9" },
  chipPartial: { backgroundColor: "#fff9c4" },
  chipMissing: { backgroundColor: "#ffcdd2" },
  mono: {
    fontFamily: "monospace",
    fontSize: 12,
    wordBreak: "break-all",
  },
  sectionTitle: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  actions: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
    marginBottom: theme.spacing(2),
  },
  capItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
}));

const statusChipClass = (classes, status) => {
  if (status === "ready") return classes.chipReady;
  if (status === "partial") return classes.chipPartial;
  return classes.chipMissing;
};

const statusLabel = status => {
  if (status === "ready") return "Pronto";
  if (status === "partial") return "Parcial";
  return "Falta / externo";
};

const Waba = () => {
  const classes = useStyles();
  const history = useHistory();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [phones, setPhones] = useState(null);
  const [profile, setProfile] = useState(null);
  const [webhooks, setWebhooks] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [profileForm, setProfileForm] = useState({
    about: "",
    description: "",
    email: "",
    address: "",
    vertical: "",
  });

  const loadOverview = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/waba/overview");
      setOverview(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  const capabilitiesByGroup = useMemo(() => {
    const caps = overview?.capabilities || [];
    return {
      messaging: caps.filter(c => c.group === "messaging"),
      business: caps.filter(c => c.group === "business"),
      manager: caps.filter(c => c.group === "manager"),
    };
  }, [overview]);

  const loadPhones = async () => {
    try {
      const { data } = await api.get("/waba/phone-numbers");
      setPhones(data);
      toast.success("Números atualizados");
    } catch (err) {
      toastError(err);
    }
  };

  const loadProfile = async () => {
    try {
      const { data } = await api.get("/waba/profile");
      setProfile(data);
      const p = data?.profile?.data?.[0] || data?.profile || {};
      setProfileForm({
        about: p.about || "",
        description: p.description || "",
        email: p.email || "",
        address: p.address || "",
        vertical: p.vertical || "",
      });
    } catch (err) {
      toastError(err);
    }
  };

  const saveProfile = async () => {
    try {
      await api.put("/waba/profile", profileForm);
      toast.success("Perfil comercial atualizado");
      await loadProfile();
    } catch (err) {
      toastError(err);
    }
  };

  const loadWebhooks = async () => {
    try {
      const { data } = await api.get("/waba/webhooks");
      setWebhooks(data);
    } catch (err) {
      toastError(err);
    }
  };

  const resubscribe = async () => {
    try {
      await api.post("/waba/webhooks/resubscribe");
      toast.success("Webhook reassinado na WABA");
      await loadWebhooks();
    } catch (err) {
      toastError(err);
    }
  };

  const loadAnalytics = async () => {
    try {
      const { data } = await api.get("/waba/analytics", { params: { days: 7 } });
      setAnalytics(data);
      if (data.warning) toast.info(data.warning);
    } catch (err) {
      toastError(err);
    }
  };

  const syncAll = async () => {
    try {
      const { data } = await api.post("/waba/sync");
      setOverview(data.overview || overview);
      setPhones(data.phones);
      setProfile(data.profile);
      setWebhooks(data.webhooks);
      toast.success("Sincronização administrativa concluída");
    } catch (err) {
      toastError(err);
    }
  };

  const renderCapabilities = list =>
    (list || []).map(cap => (
      <div key={cap.key} className={classes.capItem}>
        <Box>
          <Typography variant="body2">{cap.label}</Typography>
          <Typography variant="caption" color="textSecondary">
            {cap.detail}
          </Typography>
        </Box>
        <Chip
          size="small"
          label={statusLabel(cap.status)}
          className={statusChipClass(classes, cap.status)}
        />
      </div>
    ));

  if (loading && !overview) {
    return (
      <MainContainer>
        <Box display="flex" justifyContent="center" mt={6}>
          <CircularProgress />
        </Box>
      </MainContainer>
    );
  }

  return (
    <MainContainer>
      <MainHeader>
        <Title>{i18n.t("mainDrawer.listItems.waba")}</Title>
      </MainHeader>

      <Paper className={classes.paper} variant="outlined">
        <Typography variant="subtitle1" gutterBottom>
          Status da integração Meta / SDK
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="body2">
              Provider: <b>{overview?.provider}</b>{" "}
              {overview?.isMetaCloud ? (
                <Chip size="small" label="Meta Cloud" className={classes.chipReady} />
              ) : (
                <Chip size="small" label="Não Meta" className={classes.chipMissing} />
              )}
            </Typography>
            <Typography variant="body2" className={classes.mono}>
              App ID: {overview?.sdk?.appId || "—"}
            </Typography>
            <Typography variant="body2" className={classes.mono}>
              Config ID: {overview?.sdk?.configId || "—"}
            </Typography>
            <Typography variant="body2" className={classes.mono}>
              Graph: {overview?.graphVersion || "—"}
            </Typography>
            <Typography variant="body2" className={classes.mono}>
              WABA: {overview?.wabaId || "—"}
            </Typography>
            <Typography variant="body2" className={classes.mono}>
              Phone Number ID: {overview?.phoneNumberId || "—"}
            </Typography>
            <Typography variant="body2">
              Coexistência: {overview?.coexistence ? "sim" : "não"} · Fonte:{" "}
              {overview?.credentialsSource || "—"}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="body2" className={classes.mono}>
              Webhook: {overview?.webhookUrl || "—"}
            </Typography>
            <Typography variant="body2">
              Conta Graph: {overview?.waba?.name || "—"} · Review:{" "}
              {overview?.waba?.account_review_status || "—"}
            </Typography>
            <Typography variant="caption" color="textSecondary" display="block">
              SDK: FB.init cookie/xfbml + Embedded Signup (
              {overview?.sdk?.featureType})
            </Typography>
          </Grid>
        </Grid>

        <div className={classes.actions} style={{ marginTop: 16 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => history.push("/waba-signup")}
          >
            Conectar WhatsApp (Meta)
          </Button>
          <Button
            variant="outlined"
            startIcon={<SyncIcon />}
            onClick={syncAll}
          >
            Sincronizar admin
          </Button>
          <Button variant="outlined" onClick={loadOverview}>
            Atualizar status
          </Button>
        </div>
      </Paper>

      <Paper className={classes.paper} variant="outlined">
        <Tabs
          value={tab}
          onChange={(_e, v) => setTab(v)}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Mensagens" />
          <Tab label="Negócio / Templates" />
          <Tab label="Business Manager" />
        </Tabs>
        <Divider />

        {tab === 0 && (
          <Box mt={2}>
            <Typography variant="subtitle2" className={classes.sectionTitle}>
              Capacidades de mensagens
            </Typography>
            {renderCapabilities(capabilitiesByGroup.messaging)}

            <Typography variant="subtitle2" className={classes.sectionTitle}>
              Perfil comercial
            </Typography>
            <div className={classes.actions}>
              <Button variant="outlined" onClick={loadProfile}>
                Carregar perfil
              </Button>
              <Button variant="contained" color="primary" onClick={saveProfile}>
                Salvar perfil
              </Button>
            </div>
            <Grid container spacing={2}>
              {["about", "description", "email", "address", "vertical"].map(
                field => (
                  <Grid item xs={12} md={6} key={field}>
                    <TextField
                      fullWidth
                      label={field}
                      variant="outlined"
                      size="small"
                      value={profileForm[field]}
                      onChange={e =>
                        setProfileForm(prev => ({
                          ...prev,
                          [field]: e.target.value,
                        }))
                      }
                    />
                  </Grid>
                )
              )}
            </Grid>
            {profile?.profile?.data?.[0]?.profile_picture_url ? (
              <Box mt={2}>
                <Typography variant="caption">Foto do perfil (negócio)</Typography>
                <br />
                <img
                  src={profile.profile.data[0].profile_picture_url}
                  alt="profile"
                  style={{ maxWidth: 96, borderRadius: 8 }}
                />
              </Box>
            ) : null}

            <Typography variant="subtitle2" className={classes.sectionTitle}>
              Analytics agregados (7 dias)
            </Typography>
            <Button variant="outlined" onClick={loadAnalytics}>
              Buscar analytics
            </Button>
            {analytics ? (
              <pre className={classes.mono} style={{ marginTop: 8 }}>
                {JSON.stringify(analytics.analytics || analytics, null, 2)}
              </pre>
            ) : null}
          </Box>
        )}

        {tab === 1 && (
          <Box mt={2}>
            <Typography variant="subtitle2" className={classes.sectionTitle}>
              Capacidades de negócio
            </Typography>
            {renderCapabilities(capabilitiesByGroup.business)}

            <Typography variant="subtitle2" className={classes.sectionTitle}>
              Números comerciais
            </Typography>
            <div className={classes.actions}>
              <Button variant="outlined" onClick={loadPhones}>
                Listar números
              </Button>
            </div>
            {phones?.phoneNumbers?.length ? (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Número</TableCell>
                    <TableCell>Nome</TableCell>
                    <TableCell>Qualidade</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>wa.me / QR conversa</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {phones.phoneNumbers.map(p => {
                    const link = (phones.conversationLinks || []).find(
                      l => String(l.phoneNumberId) === String(p.id)
                    );
                    return (
                      <TableRow key={p.id}>
                        <TableCell>{p.display_phone_number}</TableCell>
                        <TableCell>{p.verified_name}</TableCell>
                        <TableCell>{p.quality_rating || "—"}</TableCell>
                        <TableCell>
                          {p.code_verification_status || p.platform_type || "—"}
                        </TableCell>
                        <TableCell>
                          {link?.waMeUrl ? (
                            <a href={link.waMeUrl} target="_blank" rel="noreferrer">
                              {link.waMeUrl}
                            </a>
                          ) : (
                            "—"
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            ) : (
              <Typography variant="body2" color="textSecondary">
                Clique em Listar números para carregar da Graph API.
              </Typography>
            )}

            <Typography variant="subtitle2" className={classes.sectionTitle}>
              Templates / textos de disparo
            </Typography>
            <MetaTemplatesManager showList showSendPanel />

            <Typography variant="subtitle2" className={classes.sectionTitle}>
              Webhooks
            </Typography>
            <div className={classes.actions}>
              <Button variant="outlined" onClick={loadWebhooks}>
                Ver assinaturas
              </Button>
              <Button variant="contained" color="primary" onClick={resubscribe}>
                Reassinar app
              </Button>
            </div>
            {webhooks ? (
              <>
                <Typography variant="body2" className={classes.mono}>
                  {webhooks.webhookUrl}
                </Typography>
                <Typography variant="caption" display="block">
                  Campos recomendados: {(webhooks.recommendedFields || []).join(", ")}
                </Typography>
                <pre className={classes.mono}>
                  {JSON.stringify(webhooks.subscribedApps, null, 2)}
                </pre>
              </>
            ) : null}
          </Box>
        )}

        {tab === 2 && (
          <Box mt={2}>
            <Typography variant="subtitle2" className={classes.sectionTitle}>
              Business Manager / ativos
            </Typography>
            {renderCapabilities(capabilitiesByGroup.manager)}

            <Box mt={2} display="flex" alignItems="center" style={{ gap: 8 }}>
              <BusinessIcon color="action" />
              <Typography variant="body2">
                Business ID: {overview?.businessId || "—"} · Verification:{" "}
                {overview?.waba?.business_verification_status || "—"}
              </Typography>
            </Box>
            <Typography variant="body2" color="textSecondary" paragraph>
              Permissões finas de usuários do Business Manager continuam no Meta
              Business Suite. Aqui sincronizamos ativos e assinaturas da API
              oficial.
            </Typography>
            <div className={classes.actions}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SyncIcon />}
                onClick={syncAll}
              >
                Sincronizar informações administrativas
              </Button>
              <Button variant="outlined" onClick={loadAnalytics}>
                Métricas agregadas
              </Button>
            </div>
          </Box>
        )}
      </Paper>
    </MainContainer>
  );
};

export default Waba;
